import React, { useState } from 'react';
import type { FaqItem } from '../content/faq';
import { renderInline } from '../legal/renderInline';

interface FaqAccordionProps {
  items: FaqItem[];
}

/**
 * Questions as an accordion: a native heading holding a button with
 * aria-expanded, and the answer in a labelled region that is always in the
 * HTML (hidden, not unmounted) so search engines and screen readers get every
 * answer. Several can be open at once; a reader comparing two answers should
 * not have to close one.
 */
export default function FaqAccordion({ items }: FaqAccordionProps) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (id: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return (
    <div className="mk-acc">
      {items.map((item) => {
        const expanded = open.has(item.id);
        const buttonId = `faq-${item.id}-button`;
        const panelId = `faq-${item.id}-panel`;
        return (
          <div key={item.id} className="mk-acc__item">
            <h3 className="mk-acc__head">
              <button
                type="button"
                id={buttonId}
                className="mk-acc__btn"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => toggle(item.id)}
              >
                <span>{item.question}</span>
                <span className="mk-acc__mark" aria-hidden="true">
                  {expanded ? '−' : '+'}
                </span>
              </button>
            </h3>
            <div id={panelId} role="region" aria-labelledby={buttonId} className="mk-acc__panel t-body" hidden={!expanded}>
              {item.answer.map((paragraph) => (
                <p key={paragraph}>{renderInline(paragraph)}</p>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
